import React, { useEffect, useRef, useState } from 'react';
import { X, Box, Loader2, AlertTriangle, Ruler, Scissors, Trash2, ArrowLeftRight } from 'lucide-react';
import api from '../api/axios';

interface IfcViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  fileName?: string;
  // Se definido, isola e mostra apenas o objeto IFC com este GlobalId (GUID).
  highlightGuid?: string;
}

// Versão do web-ifc instalada (web-ifc-three@0.0.125 -> web-ifc@0.0.39).
// O WASM é carregado do CDN apenas quando o visualizador abre.
const WASM_PATH = 'https://unpkg.com/web-ifc@0.0.39/';

type ClipAxis = '' | 'x' | 'y' | 'z';

// Interface exposta pelo motor 3D (dentro do useEffect) aos controlos React.
interface ViewerApi {
  setClip: (axis: ClipAxis, t: number, flip: boolean) => void;
  setMeasure: (on: boolean) => void;
  clearMeasures: () => void;
}

// Visualizador 3D de IFC carregado sob demanda: o three.js e o web-ifc-three
// só são importados (chunk separado) quando o modal abre, e tudo é libertado ao fechar.
const IfcViewerModal: React.FC<IfcViewerModalProps> = ({ isOpen, onClose, projectId, fileName, highlightGuid }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<ViewerApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Controlos de corte e medição (o estado vive no React; o efeito lê via viewerRef).
  const [clipAxis, setClipAxis] = useState<ClipAxis>('');
  const [clipT, setClipT] = useState(0.5);
  const [clipFlip, setClipFlip] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [measureCount, setMeasureCount] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    let disposed = false;
    let frameId = 0;
    let renderer: any = null;
    let loader: any = null;
    let blobUrl = '';
    let onResize: (() => void) | null = null;
    let labelLayer: HTMLDivElement | null = null;

    const init = async () => {
      setLoading(true);
      setError('');
      setClipAxis(''); setClipT(0.5); setClipFlip(false);
      setMeasuring(false); setMeasureCount(0);
      try {
        // Imports dinâmicos -> ficam num chunk próprio, fora do bundle principal.
        // @ts-ignore
        const THREE: any = await import('three');
        // @ts-ignore
        const { OrbitControls }: any = await import('three/examples/jsm/controls/OrbitControls.js');
        // @ts-ignore
        const { IFCLoader }: any = await import('web-ifc-three');

        const container = containerRef.current;
        if (!container || disposed) return;

        const width = container.clientWidth;
        const height = container.clientHeight;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(document.documentElement.classList.contains('dark') ? 0x0b0f1a : 0xf1f5f9);

        const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
        camera.position.set(12, 10, 12);

        renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(width, height);
        container.appendChild(renderer.domElement);

        // Camada HTML para as etiquetas das medições (por cima do canvas).
        labelLayer = document.createElement('div');
        labelLayer.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none;';
        container.appendChild(labelLayer);

        scene.add(new THREE.AmbientLight(0xffffff, 0.7));
        const dir = new THREE.DirectionalLight(0xffffff, 1.0);
        dir.position.set(10, 20, 10);
        scene.add(dir);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;

        // Carregar o ficheiro IFC (autenticado) como blob.
        const { data: blob } = await api.get(`/projects/${projectId}/ifc/download`, { responseType: 'blob' });
        if (disposed) return;
        blobUrl = URL.createObjectURL(blob);
        // Texto STEP do IFC (só lido quando precisamos de localizar um objeto por GUID).
        const ifcText: string = highlightGuid ? await blob.text() : '';

        loader = new IFCLoader();
        // setWasmPath() do web-ifc-three não marca o caminho como absoluto, por isso um URL
        // de CDN seria tratado como relativo (e o dev server devolveria HTML -> erro de WASM).
        // Chamamos a API por baixo com absolute=true.
        loader.ifcManager.state.api.SetWasmPath(WASM_PATH, true);
        // Recentra geometrias georreferenciadas junto à origem (senão o modelo fica fora do ecrã).
        try {
          await loader.ifcManager.applyWebIfcConfig({ COORDINATE_TO_ORIGIN: true, USE_FAST_BOOLS: true });
        } catch {}

        // Box do que está visível (modelo completo ou subset) — usada para enquadrar a
        // câmara, dimensionar os marcadores de medição e posicionar o plano de corte.
        const modelBox = new THREE.Box3();
        let modelSize = 10;
        // Objetos onde o raycast das medições pode acertar.
        const pickables: any[] = [];

        // Enquadra a câmara numa box.
        const frameBox = (box: any) => {
          if (box.isEmpty()) return;
          modelBox.copy(box);
          modelSize = box.getSize(new THREE.Vector3()).length() || 10;
          const center = box.getCenter(new THREE.Vector3());
          const size = modelSize;
          controls.target.copy(center);
          camera.position.set(center.x + size * 0.7, center.y + size * 0.6, center.z + size * 0.7);
          camera.near = Math.max(size / 1000, 0.01);
          camera.far = size * 1000 + 1000;
          camera.updateProjectionMatrix();
          controls.update();
        };

        // Box a partir de toda a geometria do objeto (para o modelo completo).
        const fitTo = (obj: any) => frameBox(new THREE.Box3().setFromObject(obj));

        // Box "apertada" calculada só sobre os vértices realmente indexados pelo subset.
        // (Um subset partilha o buffer de posições do modelo inteiro, por isso setFromObject
        // daria a box do edifício todo — deixando o elemento minúsculo e descentrado.)
        const fitToSubset = (mesh: any) => {
          const geom = mesh?.geometry;
          const pos = geom?.attributes?.position;
          if (!pos) { fitTo(mesh); return; }
          mesh.updateWorldMatrix(true, false);
          const box = new THREE.Box3();
          const v = new THREE.Vector3();
          const idx = geom.index;
          const n = idx ? idx.count : pos.count;
          for (let i = 0; i < n; i++) {
            const vi = idx ? idx.getX(i) : i;
            v.fromBufferAttribute(pos, vi).applyMatrix4(mesh.matrixWorld);
            box.expandByPoint(v);
          }
          if (box.isEmpty()) { fitTo(mesh); return; }
          frameBox(box);
        };

        // --- Cortes: um plano de clipping global movido ao longo de um eixo ---
        const clipPlane = new THREE.Plane();
        let clipActive = false;
        const setClip = (axis: ClipAxis, t: number, flip: boolean) => {
          if (!axis || modelBox.isEmpty()) {
            renderer.clippingPlanes = [];
            clipActive = false;
            return;
          }
          const normal = new THREE.Vector3(axis === 'x' ? 1 : 0, axis === 'y' ? 1 : 0, axis === 'z' ? 1 : 0);
          if (!flip) normal.negate(); // por defeito esconde o que está acima/à frente da posição
          const p = modelBox.min[axis] + (modelBox.max[axis] - modelBox.min[axis]) * t;
          const point = modelBox.getCenter(new THREE.Vector3());
          point[axis] = p;
          clipPlane.setFromNormalAndCoplanarPoint(normal, point);
          renderer.clippingPlanes = [clipPlane];
          clipActive = true;
        };

        // --- Medição de distâncias: dois cliques no modelo -> linha + etiqueta ---
        const raycaster = new THREE.Raycaster();
        const measure: { on: boolean; ptA: any; tempMarker: any; records: any[] } =
          { on: false, ptA: null, tempMarker: null, records: [] };
        const markerMat = new THREE.MeshBasicMaterial({ color: 0x4f46e5, depthTest: false, transparent: true });
        const lineMat = new THREE.LineBasicMaterial({ color: 0x4f46e5, depthTest: false, transparent: true, linewidth: 2 });

        const makeMarker = (pt: any) => {
          const s = new THREE.Mesh(new THREE.SphereGeometry(Math.max(modelSize * 0.004, 0.01), 16, 16), markerMat);
          s.position.copy(pt);
          s.renderOrder = 999;
          scene.add(s);
          return s;
        };

        const makeLabel = (text: string) => {
          const el = document.createElement('div');
          el.textContent = text;
          el.style.cssText = 'position:absolute;left:0;top:0;padding:2px 8px;border-radius:8px;' +
            'background:#4f46e5;color:#fff;font-size:11px;font-weight:600;white-space:nowrap;' +
            'box-shadow:0 2px 6px rgba(0,0,0,.35);';
          labelLayer!.appendChild(el);
          return el;
        };

        const pickPoint = (ev: PointerEvent) => {
          const rect = renderer.domElement.getBoundingClientRect();
          const ndc = new THREE.Vector2(
            ((ev.clientX - rect.left) / rect.width) * 2 - 1,
            -((ev.clientY - rect.top) / rect.height) * 2 + 1
          );
          raycaster.setFromCamera(ndc, camera);
          const hits = raycaster.intersectObjects(pickables, true);
          for (const h of hits) {
            // Ignora geometria escondida pelo plano de corte ativo.
            if (clipActive && clipPlane.distanceToPoint(h.point) < 0) continue;
            return h.point.clone();
          }
          return null;
        };

        const addMeasurePoint = (pt: any) => {
          if (!measure.ptA) {
            measure.ptA = pt;
            measure.tempMarker = makeMarker(pt);
            return;
          }
          const a = measure.ptA; measure.ptA = null;
          const b = pt;
          const dist = a.distanceTo(b);
          const geom = new THREE.BufferGeometry().setFromPoints([a, b]);
          const line = new THREE.Line(geom, lineMat);
          line.renderOrder = 999;
          scene.add(line);
          const mB = makeMarker(b);
          const record = {
            line, markers: [measure.tempMarker, mB],
            mid: a.clone().add(b).multiplyScalar(0.5),
            label: makeLabel(`${dist.toFixed(2)} m`),
          };
          measure.tempMarker = null;
          measure.records.push(record);
          setMeasureCount(measure.records.length);
        };

        const clearMeasures = () => {
          for (const r of measure.records) {
            scene.remove(r.line); r.line.geometry.dispose();
            r.markers.forEach((m: any) => { scene.remove(m); m.geometry.dispose(); });
            r.label.remove();
          }
          measure.records = [];
          if (measure.tempMarker) { scene.remove(measure.tempMarker); measure.tempMarker.geometry.dispose(); }
          measure.tempMarker = null;
          measure.ptA = null;
          setMeasureCount(0);
        };

        const setMeasure = (on: boolean) => {
          measure.on = on;
          renderer.domElement.style.cursor = on ? 'crosshair' : '';
          if (!on && measure.tempMarker) {
            // descarta o primeiro ponto de uma medição incompleta
            scene.remove(measure.tempMarker); measure.tempMarker.geometry.dispose();
            measure.tempMarker = null; measure.ptA = null;
          }
        };

        // Distingue clique de arrasto (para não medir enquanto se roda o modelo).
        let downPos: { x: number; y: number } | null = null;
        const onPointerDown = (ev: PointerEvent) => { downPos = { x: ev.clientX, y: ev.clientY }; };
        const onPointerUp = (ev: PointerEvent) => {
          if (!measure.on || !downPos || ev.button !== 0) { downPos = null; return; }
          const moved = Math.hypot(ev.clientX - downPos.x, ev.clientY - downPos.y);
          downPos = null;
          if (moved > 5) return;
          const pt = pickPoint(ev);
          if (pt) addMeasurePoint(pt);
        };
        renderer.domElement.addEventListener('pointerdown', onPointerDown);
        renderer.domElement.addEventListener('pointerup', onPointerUp);

        // Reposiciona as etiquetas das medições (projeção 3D -> ecrã) a cada frame.
        const updateLabels = () => {
          if (!measure.records.length) return;
          const w = renderer.domElement.clientWidth;
          const h = renderer.domElement.clientHeight;
          const v = new THREE.Vector3();
          for (const r of measure.records) {
            v.copy(r.mid).project(camera);
            if (v.z > 1) { r.label.style.display = 'none'; continue; }
            r.label.style.display = 'block';
            const x = (v.x * 0.5 + 0.5) * w;
            const y = (-v.y * 0.5 + 0.5) * h;
            r.label.style.transform = `translate(${x}px,${y}px) translate(-50%,-130%)`;
          }
        };

        viewerRef.current = { setClip, setMeasure, clearMeasures };

        // Localiza o expressID e o tipo do elemento cujo GlobalId == guid, lendo o texto STEP.
        // (Independente da versão do web-ifc, que não desserializa todos os esquemas IFC4X3.)
        const findEntityByGuid = (guid: string): { eid: number; type: string } | null => {
          const esc = guid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const m = ifcText.match(new RegExp("#(\\d+)\\s*=\\s*([A-Za-z0-9_]+)\\s*\\(\\s*'" + esc + "'"));
          return m ? { eid: parseInt(m[1], 10), type: m[2] } : null;
        };

        loader.load(
          blobUrl,
          async (model: any) => {
            if (disposed) return;
            const modelID = model.modelID;

            if (highlightGuid) {
              // Isola e mostra apenas o objeto IFC correspondente ao GUID.
              const match = findEntityByGuid(highlightGuid);
              if (disposed) return;
              if (!match) {
                setError(`Objeto IFC (${highlightGuid}) não encontrado neste modelo.`);
                setLoading(false);
                return;
              }
              // Confirma que o elemento tem geometria (está entre os meshes do modelo).
              const renderable = new Set<number>();
              loader.ifcManager.state.api.StreamAllMeshes(modelID, (mesh: any) => { renderable.add(mesh.expressID); });
              if (!renderable.has(match.eid)) {
                setError(`O objeto ${match.type} não tem geometria 3D no modelo.`);
                setLoading(false);
                return;
              }
              const subset = loader.ifcManager.createSubset({
                modelID,
                ids: [match.eid],
                scene,
                removePrevious: true,
                customID: 'sensor-object',
              });
              if (subset) { fitToSubset(subset); pickables.push(subset); }
            } else {
              scene.add(model);
              fitTo(model);
              pickables.push(model);
            }
            setLoading(false);
          },
          undefined,
          (err: any) => {
            if (disposed) return;
            console.error(err);
            setError('Não foi possível interpretar o ficheiro IFC.');
            setLoading(false);
          }
        );

        const animate = () => {
          if (disposed) return;
          frameId = requestAnimationFrame(animate);
          controls.update();
          updateLabels();
          renderer.render(scene, camera);
        };
        animate();

        onResize = () => {
          if (!container) return;
          const w = container.clientWidth;
          const h = container.clientHeight;
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h);
        };
        window.addEventListener('resize', onResize);
      } catch (e) {
        console.error(e);
        if (!disposed) {
          setError('Não foi possível carregar o visualizador 3D.');
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      disposed = true;
      viewerRef.current = null;
      if (frameId) cancelAnimationFrame(frameId);
      if (onResize) window.removeEventListener('resize', onResize);
      try { loader?.ifcManager?.dispose?.(); } catch {}
      try {
        renderer?.dispose?.();
        renderer?.domElement?.remove();
      } catch {}
      try { labelLayer?.remove(); } catch {}
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [isOpen, projectId, highlightGuid]);

  if (!isOpen) return null;

  const toggleAxis = (axis: ClipAxis) => {
    const next = clipAxis === axis ? '' : axis;
    setClipAxis(next);
    viewerRef.current?.setClip(next, clipT, clipFlip);
  };

  const onClipSlide = (t: number) => {
    setClipT(t);
    viewerRef.current?.setClip(clipAxis, t, clipFlip);
  };

  const toggleFlip = () => {
    setClipFlip(f => {
      viewerRef.current?.setClip(clipAxis, clipT, !f);
      return !f;
    });
  };

  const toggleMeasure = () => {
    setMeasuring(m => {
      viewerRef.current?.setMeasure(!m);
      return !m;
    });
  };

  const axisBtn = (axis: ClipAxis, label: string) => (
    <button
      key={axis}
      onClick={() => toggleAxis(axis)}
      className={`px-2 py-1 rounded-lg text-[11px] font-bold transition-colors ${
        clipAxis === axis
          ? 'bg-indigo-600 text-white'
          : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[3400] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white dark:bg-[#161b26] rounded-3xl border border-slate-200 dark:border-slate-800 max-w-5xl w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300 flex flex-col" style={{ height: '80vh' }}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-indigo-600/10 text-indigo-600 rounded-xl shrink-0"><Box size={18} /></div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-900 dark:text-white truncate">{highlightGuid ? `Objeto IFC · ${highlightGuid}` : (fileName || 'Modelo IFC')}</p>
              <p className="text-[11px] text-slate-400">Arrasta para rodar · scroll para zoom · {measuring ? 'clica em 2 pontos do modelo para medir' : 'usa a barra para cortes e medições'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors shrink-0"><X size={20} className="text-slate-400" /></button>
        </div>

        <div className="relative flex-1 bg-slate-100 dark:bg-[#0b0f1a]">
          <div ref={containerRef} className="absolute inset-0" />

          {/* Barra de ferramentas: cortes e medição */}
          {!loading && !error && (
            <div className="absolute top-3 left-3 z-10 flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 p-1 bg-white/95 dark:bg-[#161b26]/95 backdrop-blur rounded-xl border border-slate-200 dark:border-slate-800 shadow-lg">
                <span className="pl-2 pr-1 flex items-center gap-1 text-[11px] font-bold text-slate-400 uppercase"><Scissors size={12} /> Corte</span>
                {axisBtn('x', 'X')}
                {axisBtn('y', 'Y')}
                {axisBtn('z', 'Z')}
                {clipAxis && (
                  <>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.005}
                      value={clipT}
                      onChange={(e) => onClipSlide(parseFloat(e.target.value))}
                      className="w-28 mx-1 accent-indigo-600"
                    />
                    <button
                      onClick={toggleFlip}
                      title="Inverter o lado do corte"
                      className={`p-1.5 rounded-lg transition-colors ${clipFlip ? 'bg-indigo-600 text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                    >
                      <ArrowLeftRight size={13} />
                    </button>
                  </>
                )}
              </div>

              <div className="flex items-center gap-1 p-1 bg-white/95 dark:bg-[#161b26]/95 backdrop-blur rounded-xl border border-slate-200 dark:border-slate-800 shadow-lg">
                <button
                  onClick={toggleMeasure}
                  className={`px-2 py-1 rounded-lg text-[11px] font-bold flex items-center gap-1.5 transition-colors ${
                    measuring ? 'bg-indigo-600 text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <Ruler size={13} /> Medir
                </button>
                {measureCount > 0 && (
                  <button
                    onClick={() => viewerRef.current?.clearMeasures()}
                    title="Apagar todas as medições"
                    className="px-2 py-1 rounded-lg text-[11px] font-bold flex items-center gap-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <Trash2 size={13} /> Limpar ({measureCount})
                  </button>
                )}
              </div>
            </div>
          )}

          {loading && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-slate-400">
              <Loader2 size={28} className="animate-spin text-indigo-500" />
              <p className="text-xs font-medium">A carregar o modelo 3D…</p>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
              <AlertTriangle size={28} className="text-amber-500" />
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IfcViewerModal;
